'use client';

import { memo } from 'react';
import { useAuth } from '../auth/hooks/useAuth';
import { AUTH_ROUTE, DEMO_ROUTE, LOGIN_ROUTE, PRICING_ROUTE, RESET_PASSWORD } from '../constants/routes';
import MobileSlideout from './MobileSlideout';
import usePageLoaded from '@/machinery/usePageLoaded';
import LoadingCard from '../loading/components/LoadingCard';
import LogoTextHorizontal from '../common/components/LogoTextHorizontal';
import { Dropdown, DropdownMenu, DropdownTrigger, DropdownItem, Button, Tabs, Tab } from '@heroui/react';
import { Link, useLocation, useNavigate } from '@tanstack/react-router';

const IGNORE_HEADER_ROUTES = [RESET_PASSWORD];

/** Tab keys must stay in sync with HeroUI `Tabs` selection (controlled + router). */
const LOGGED_OUT_TAB_KEYS = ['/', DEMO_ROUTE, PRICING_ROUTE, LOGIN_ROUTE] as const;

function loggedOutTabSelectedKey(pathname: string): (typeof LOGGED_OUT_TAB_KEYS)[number] {
    if (pathname === '/' || pathname === '') return '/';
    if (pathname.startsWith(DEMO_ROUTE)) return DEMO_ROUTE;
    if (pathname.startsWith(PRICING_ROUTE)) return PRICING_ROUTE;
    if (pathname.startsWith(AUTH_ROUTE)) return LOGIN_ROUTE;
    return '/';
}

function Header() {
    const { user, logout, userLoading } = useAuth();
    const location = useLocation();
    const pathname = location.pathname;
    const pageLoaded = usePageLoaded();
    const navigate = useNavigate();

    if (IGNORE_HEADER_ROUTES.some(route => location.pathname?.includes(route))) {
        return <div></div>;
    }

    return (
        <div className='z-50 flex h-16 items-center border-b border-b-gray-200 px-4 md:grid md:grid-cols-2 md:px-12 '>
            <MobileSlideout />
            <div className='text-xl font-bold'>
                <div className='h-16 py-3'>
                    <LogoTextHorizontal />
                </div>
            </div>

            {pageLoaded && !userLoading ? (
                <div
                    className={` hidden  grid-flow-col items-center justify-items-center justify-self-end text-lg font-semibold md:grid`}>
                    {!user ? (
                        <Tabs
                            variant='underlined'
                            selectedKey={loggedOutTabSelectedKey(pathname)}
                            onSelectionChange={key => {
                                const to = String(key);
                                if (
                                    LOGGED_OUT_TAB_KEYS.includes(to as (typeof LOGGED_OUT_TAB_KEYS)[number])
                                ) {
                                    navigate({ to });
                                }
                            }}
                            color='primary'
                            size='lg'>
                            <Tab key='/' title='Home' />
                            <Tab key={DEMO_ROUTE} title='Demo' />
                            <Tab key={PRICING_ROUTE} title='Pricing' />
                            <Tab key={LOGIN_ROUTE} title='Log in' />
                        </Tabs>
                    ) : (
                        <>
                            <Tabs
                                variant='underlined'
                                selectedKey={pathname?.split('/')[1]}
                                color='primary'
                                size='lg'>
                                {/* @ts-expect-error tab-link */}
                                <Tab as={Link} to='/dashboard' key='dashboard' title='Dashboard' />
                                {/* @ts-expect-error tab-link */}
                                <Tab as={Link} to='/rounds' key='rounds' title='Rounds' />
                            </Tabs>
                            <Dropdown>
                                <DropdownTrigger>
                                    <Button
                                        size='lg'
                                        className='bg-transparent font-semibold text-[#71717A] hover:bg-transparent focus:bg-transparent'>
                                        Account
                                    </Button>
                                </DropdownTrigger>
                                <DropdownMenu aria-label='Link Actions'>
                                    <DropdownItem
                                        key='home'
                                        onPress={() => navigate({ to: '/account/settings' })}>
                                        Settings
                                    </DropdownItem>
                                    <DropdownItem
                                        key='teams'
                                        onPress={() => navigate({ to: '/account/teams' })}>
                                        Teams
                                    </DropdownItem>
                                    <DropdownItem key='courses' onPress={() => navigate({ to: '/courses' })}>
                                        Courses
                                    </DropdownItem>
                                    <DropdownItem
                                        className={!user?.is_admin ? 'hidden' : ''}
                                        key={'admin'}
                                        onPress={() => navigate({ to: '/account/admin' })}>
                                        Admin
                                    </DropdownItem>
                                    <DropdownItem
                                        color='danger'
                                        className='text-danger'
                                        key={'log-out'}
                                        onPress={() => {
                                            close();
                                            logout();
                                        }}>
                                        Log out
                                    </DropdownItem>
                                </DropdownMenu>
                            </Dropdown>
                        </>
                    )}
                </div>
            ) : (
                <div className='grid  justify-self-end'>
                    <LoadingCard />
                </div>
            )}
        </div>
    );
}

export default memo(Header);
